"use client"

import { cn, Popover, PopoverContent, PopoverTrigger, Switch, Tooltip } from "@heroui/react"
import { useQuery } from "@tanstack/react-query"
import { useTranslations } from "next-intl"
import { useState } from "react"
import { AssetModelType, pickBestOff, type AssetModel } from "@/apis/asset"
import { assetModelsQueryOption } from "@/apis/query/asset"
import { FireGlyph, ModelOptionList, OffBadge, toModelOption } from "./ModelSelect"
import { SelectImageModal, type PickedImage } from "./SelectImageModal"

/** 创作偏好：不选（auto/空）则由 Agent 自行决定 */
export type CreationPrefs = {
  kind: "auto" | "image" | "video"
  modelId?: number
  modelName?: string
  resolution?: string
  seconds?: number
  ratio?: string
  refs: PickedImage[]
  /** 开启后创建会话直接进入画布模式生成 */
  canvasMode: boolean
}

export const emptyPrefs = (): CreationPrefs => ({ kind: "auto", refs: [], canvasMode: false })

/**
 * 偏好拼接为首条消息的附加段（用户语言）；全为自动时返回空串。
 * Agent 侧凭模型能力表把别名映射为 model_id。
 */
export function buildPrefsText(prefs: CreationPrefs, t: ReturnType<typeof useTranslations<"generatePage">>): string {
  const parts: string[] = []
  if (prefs.kind !== "auto") parts.push(`${t("prefKind")}: ${prefs.kind === "image" ? t("prefImage") : t("prefVideo")}`)
  if (prefs.modelName) parts.push(`${t("prefModel")}: ${prefs.modelName}`)
  if (prefs.kind === "video" && prefs.resolution) parts.push(`${t("prefResolution")}: ${prefs.resolution}`)
  if (prefs.kind === "video" && prefs.seconds) parts.push(`${t("prefSeconds")}: ${prefs.seconds}s`)
  if (prefs.ratio) parts.push(`${t("prefRatio")}: ${prefs.ratio}`)

  const lines: string[] = []
  if (parts.length) lines.push(`[${t("prefTitle")}] ${parts.join("; ")}`)
  if (prefs.refs.length) lines.push(`[${t("prefRefs")}] ${prefs.refs.map((r) => r.url).join(" ")}`)
  return lines.length ? `\n\n${lines.join("\n")}` : ""
}

const ChevronIcon = ({ className }: { className?: string }) => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" className={className}>
    <path d="m2 3.8 3 3 3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

export const AtGlyph = ({ className }: { className?: string }) => (
  <svg width="12" height="12" viewBox="0 0 14 14" fill="none" className={className}>
    <circle cx="7" cy="7" r="2.2" stroke="currentColor" strokeWidth="1.2" />
    <path d="M9.2 4.8v3.1c0 1 .6 1.6 1.4 1.6 1 0 1.6-.9 1.6-2.5A5.2 5.2 0 1 0 9.6 11.6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
)

export type PrefOption = {
  value: string
  /** 下拉选项展示（可带价格/免费次数等附加信息） */
  label: string
  /** 触发器上的简短值（缺省用 label）：附加信息只出现在下拉里，保持 chip 紧凑 */
  short?: string
  /** 该档位折扣系数 0–100；有折扣时下拉展示 off 角标 */
  off?: number
}

/** 生成宽高比档位（后端默认 9:16；图片/视频/快速生成共用） */
export const RATIO_OPTIONS = ["9:16", "16:9", "1:1", "3:4", "4:3"]

/**
 * 工具栏触发器统一样式（agent 偏好条同款）：无边框"图标/标签+值"紧排。
 * 模型选择、素材、技能、偏好 chip 等输入工具栏入口统一使用。
 */
export const PREF_TRIGGER_CLASS =
  "flex h-7 shrink-0 items-center gap-1 px-1.5 text-xs font-medium text-theme-primary/70 transition-colors hover:text-theme-primary"

/**
 * 偏好标签（对齐小云雀顶部标签条）：无边框无底色，"标签 值"紧排；
 * 默认显示"自动"，选中后值加粗。快速生成工具栏（GenerateInput）同款复用。
 */
export function PrefChip({
  label,
  options,
  value,
  onChange,
  tip,
}: {
  label: string
  options: PrefOption[]
  value?: string
  onChange: (value: string) => void
  /** 悬停说明 */
  tip?: string
}) {
  const t = useTranslations("generatePage")
  const active = !!value
  const currentOption = options.find((o) => o.value === value)
  const currentLabel = currentOption?.short ?? currentOption?.label ?? value
  const trigger = (
    <Popover placement="bottom-start" offset={6}>
      <PopoverTrigger>
        <button type="button" className={PREF_TRIGGER_CLASS}>
          <span className="font-normal text-theme-primary/50">{label}</span>
          <span className={cn("text-theme-primary/60", active && "font-semibold text-theme-primary")}>{active ? currentLabel : t("prefAuto")}</span>
          <ChevronIcon className="size-2.5 opacity-40" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="min-w-40 items-stretch rounded-lg border border-theme-primary/10 bg-surface p-1 shadow-md">
        <div className="flex flex-col">
          {options.map((option) => {
            const selected = (option.value || undefined) === (value || undefined)
            return (
              <button
                key={option.value || "auto"}
                type="button"
                onClick={() => onChange(option.value)}
                className={cn(
                  "flex items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-xs transition-colors",
                  selected ? "bg-purple-blue/10 font-medium text-purple-blue" : "text-theme-primary/75 hover:bg-theme-primary/5",
                )}
              >
                <span className="min-w-0 flex-1">{option.label}</span>
                <OffBadge off={option.off} />
                {selected && (
                  <svg width="10" height="10" viewBox="0 0 12 12" fill="none" className="shrink-0">
                    <path d="M10 3 4.9 8.6 2 5.8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
  return tip ? (
    <Tooltip content={tip} placement="bottom" offset={8} classNames={{ content: "rounded-lg bg-theme-primary px-2.5 py-1.5 text-xs text-surface shadow-md" }}>
      <span className="inline-flex shrink-0">{trigger}</span>
    </Tooltip>
  ) : (
    trigger
  )
}

/**
 * 模型偏好 chip：触发器与其他偏好项同款（"模型 值"），
 * 弹层为带介绍的模型列表（图标 / 名称 / 免费角标 / 两行介绍，快速生成同款），
 * 顶部保留"自动"（交给 Agent 决定）。
 */
function ModelChip({
  models,
  value,
  onChange,
  tip,
  allowAuto = true,
}: {
  models: AssetModel[]
  /** 当前选中的模型 id（空 = 自动） */
  value?: number
  onChange: (id?: number, name?: string) => void
  tip?: string
  /** 是否提供"自动"项（快速生成必选具体模型时关闭） */
  allowAuto?: boolean
}) {
  const t = useTranslations("generatePage")
  const [open, setOpen] = useState(false)
  const options = models.map(toModelOption)
  const current = value ? options.find((o) => o.key === String(value)) : undefined

  const trigger = (
    <Popover isOpen={open} onOpenChange={setOpen} placement="bottom-start" offset={6}>
      <PopoverTrigger>
        <button type="button" className={PREF_TRIGGER_CLASS}>
          <span className="font-normal text-theme-primary/50">{t("prefModel")}</span>
          <span className={cn("inline-flex items-center gap-0.5", current ? "font-semibold text-theme-primary" : "font-normal text-theme-primary/60")}>
            {current?.name ?? t("prefAuto")}
            {current?.isFire && <FireGlyph className="size-3.5 shrink-0" />}
          </span>
          <ChevronIcon className="size-2.5 opacity-40" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[380px] max-w-[calc(100vw-32px)] items-stretch rounded-xl border border-theme-primary/10 bg-surface p-1.5 shadow-md">
        {/* 自动：交给 Agent 按需求挑模型 */}
        {allowAuto && (
          <button
            type="button"
            onClick={() => {
              onChange(undefined, undefined)
              setOpen(false)
            }}
            className={cn(
              "mb-1 flex items-center gap-2 rounded-xl px-3 py-2 text-left text-sm transition-colors",
              !current ? "bg-card-chip-bg font-medium text-theme-primary" : "text-theme-primary/75 hover:bg-create-card",
            )}
          >
            {t("prefModelAuto")}
            {!current && (
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="ml-auto text-purple-blue">
                <path d="M10 3 4.9 8.6 2 5.8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </button>
        )}
        <ModelOptionList
          options={options}
          selectedKey={current?.key}
          onSelect={(key) => {
            const picked = models.find((m) => String(m.id) === key)
            onChange(picked?.id, picked?.name)
            setOpen(false)
          }}
        />
      </PopoverContent>
    </Popover>
  )

  return tip ? (
    <Tooltip content={tip} placement="bottom" offset={8} classNames={{ content: "rounded-lg bg-theme-primary px-2.5 py-1.5 text-xs text-surface shadow-md" }}>
      <span className="inline-flex shrink-0">{trigger}</span>
    </Tooltip>
  ) : (
    trigger
  )
}

/**
 * 创作工具栏（快速生成 / 创作 Agent / 短剧 Agent 唯一共用组件）：
 * 技能(leading) + 类型 + 模型 + 分辨率 + 时长 + 比例 + @素材 + 画布开关，
 * 按场景以 props 控制显隐与选项来源：
 * - Agent / 短剧：档位可留空（自动，交给 Agent 决定），档位为通用硬编码；
 * - 快速生成：noAuto 必选具体值，分辨率/时长按所选模型能力表下发（resolutionOptions/secondsOptions），
 *   @素材由外部插槽（refsSlot）接管（引用进提示词而非偏好）。
 */
export function PrefsBar({
  prefs,
  onChange,
  lockKind,
  noAuto = false,
  resolutionOptions,
  secondsOptions,
  refsSlot,
  showCanvasToggle = true,
  leading,
  className,
}: {
  prefs: CreationPrefs
  onChange: (next: CreationPrefs) => void
  /** 固定类型（短剧固定 video；快速生成传当前模式），锁定时不显示类型 chip */
  lockKind?: "image" | "video"
  /** 所有档位必选具体值（快速生成）：模型/分辨率/时长/比例不提供"自动"项 */
  noAuto?: boolean
  /** 覆盖分辨率档位（快速生成按模型能力生成，标签可带价格/免费次数）；kind=image 时提供才显示 */
  resolutionOptions?: PrefOption[]
  /** 覆盖时长档位（快速生成按模型能力生成） */
  secondsOptions?: PrefOption[]
  /** 覆盖内置 @素材（快速生成用提示词 @ 引用按钮） */
  refsSlot?: React.ReactNode
  showCanvasToggle?: boolean
  /** 前置插槽（如技能选择），与偏好同一排 */
  leading?: React.ReactNode
  className?: string
}) {
  const t = useTranslations("generatePage")
  const [pickerOpen, setPickerOpen] = useState(false)
  const { data } = useQuery(assetModelsQueryOption)

  const kind = lockKind ?? prefs.kind
  const models = (data?.data?.models ?? []).filter((m) =>
    kind === "video" ? m.type === AssetModelType.Video : m.type === AssetModelType.Image,
  )
  const showVideoOpts = kind === "video"
  const patch = (p: Partial<CreationPrefs>) => onChange({ ...prefs, ...p })

  /**
   * 视频分辨率/时长档位按模型能力聚合（部分模型已支持 30s，不再硬编码 5/10/15）：
   * 选中具体模型用其能力表；"自动"取全部视频模型并集（Agent 会按偏好挑支持的模型）。
   * 连续区间模型（second_min/max）展开为落在区间内的常用档位。
   */
  const derivedVideoTiers = (() => {
    if (!showVideoOpts) return null
    const source = prefs.modelId ? models.filter((m) => m.id === prefs.modelId) : models
    const sizes = new Set<string>()
    const seconds = new Set<number>()
    for (const model of source) {
      for (const support of model.video_support ?? []) {
        sizes.add(support.size)
        const scene = support.scenes.find((s) => s.scene === "r2v")
        for (const option of scene?.second_options ?? []) seconds.add(option.second)
        const lo = Math.max(1, Number(scene?.second_min ?? 0))
        const hi = Number(scene?.second_max ?? 0)
        if (hi > 0) {
          for (const preset of [5, 10, 15, 20, 30]) {
            if (preset >= lo && preset <= hi) seconds.add(preset)
          }
        }
      }
    }
    return {
      sizes: [...sizes].sort((a, b) => parseInt(a) - parseInt(b)),
      seconds: [...seconds].sort((a, b) => a - b),
      source,
    }
  })()

  // 档位来源：外部（快速生成按所选模型下发）优先，其次模型能力聚合，模型未加载时回退通用档位
  const resOptions =
    resolutionOptions ??
    (showVideoOpts
      ? (derivedVideoTiers?.sizes.length ? derivedVideoTiers.sizes : ["480p", "720p", "1080p"]).map(
          (size) => ({
            value: size,
            label: size,
            off: pickBestOff(
              (derivedVideoTiers?.source ?? []).flatMap((m) =>
                (m.video_support ?? []).filter((v) => v.size === size).map((v) => v.off),
              ),
            ),
          }),
        )
      : undefined)
  const secOptions =
    secondsOptions ??
    (showVideoOpts
      ? (derivedVideoTiers?.seconds.length ? derivedVideoTiers.seconds : [5, 10, 15]).map(
          (second) => ({ value: String(second), label: `${second}s` }),
        )
      : undefined)

  return (
    <div className={cn("flex flex-wrap items-center gap-x-1.5 gap-y-1", className)}>
      {leading}

      {/* 类型（锁定时不显示） */}
      {!lockKind && (
        <PrefChip
          label={t("prefKind")}
          tip={t("prefKindTip")}
          value={prefs.kind === "auto" ? undefined : prefs.kind}
          onChange={(value) =>
            onChange({
              ...prefs,
              kind: (value || "auto") as CreationPrefs["kind"],
              modelId: undefined,
              modelName: undefined,
              resolution: undefined,
              seconds: undefined,
            })
          }
          options={[
            { value: "", label: t("prefKindAuto") },
            { value: "image", label: t("prefImage") },
            { value: "video", label: t("prefVideo") },
          ]}
        />
      )}

      {/* 模型（类型明确时可选）：带介绍的模型列表 */}
      {(kind === "image" || kind === "video") && models.length > 0 && (
        <ModelChip
          models={models}
          value={prefs.modelId}
          tip={noAuto ? undefined : t("prefModelTip")}
          allowAuto={!noAuto}
          onChange={(id, name) => patch({ modelId: id, modelName: name })}
        />
      )}

      {/* 分辨率 / 时长 */}
      {(resOptions?.length ?? 0) > 0 && (
        <PrefChip
          label={t("prefResolution")}
          tip={noAuto ? undefined : t("prefResolutionTip")}
          value={prefs.resolution}
          onChange={(value) => patch({ resolution: value || undefined })}
          options={[
            ...(noAuto ? [] : [{ value: "", label: t("prefResolutionAuto") }]),
            ...(resOptions ?? []),
          ]}
        />
      )}
      {(secOptions?.length ?? 0) > 0 && (
        <PrefChip
          label={t("prefSeconds")}
          tip={noAuto ? undefined : t("prefSecondsTip")}
          value={prefs.seconds ? String(prefs.seconds) : undefined}
          onChange={(value) => patch({ seconds: value ? Number(value) : undefined })}
          options={[
            ...(noAuto ? [] : [{ value: "", label: t("prefSecondsAuto") }]),
            ...(secOptions ?? []),
          ]}
        />
      )}

      {/* 比例 */}
      <PrefChip
        label={t("prefRatio")}
        tip={noAuto ? undefined : t("prefRatioTip")}
        value={prefs.ratio}
        onChange={(value) => patch({ ratio: value || undefined })}
        options={[
          ...(noAuto ? [] : [{ value: "", label: t("prefRatioAuto") }]),
          ...RATIO_OPTIONS.map((r) => ({ value: r, label: r })),
        ]}
      />

      {/* @素材：外部插槽优先（快速生成的提示词 @ 引用），否则内置选图偏好 */}
      {refsSlot ?? (
        <>
          <Tooltip
            content={t("prefRefsTip")}
            placement="bottom"
            offset={8}
            classNames={{ content: "rounded-lg bg-theme-primary px-2.5 py-1.5 text-xs text-surface shadow-md" }}
          >
            <button type="button" onClick={() => setPickerOpen(true)} className={PREF_TRIGGER_CLASS}>
              <AtGlyph className="size-3 opacity-60" />
              <span className={cn(prefs.refs.length && "font-semibold text-theme-primary")}>
                {prefs.refs.length ? t("prefRefsCount", { count: prefs.refs.length }) : t("prefRefsBtn")}
              </span>
            </button>
          </Tooltip>
          {prefs.refs.map((ref, i) => (
            <span key={i} className="relative shrink-0">
              <img src={ref.url} alt="" className="size-7 rounded-md border border-theme-primary/10 object-cover" />
              <button
                type="button"
                aria-label="remove"
                onClick={() => patch({ refs: prefs.refs.filter((_, j) => j !== i) })}
                className="absolute -right-1 -top-1 grid size-3.5 place-content-center rounded-full bg-theme-primary/70 text-white"
              >
                <svg width="6" height="6" viewBox="0 0 8 8" fill="none">
                  <path d="m1 1 6 6M7 1 1 7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
              </button>
            </span>
          ))}
        </>
      )}

      {/* 画布模式：滑动开关 */}
      {showCanvasToggle && (
        <Tooltip
          content={t("canvasModeTip")}
          placement="bottom"
          offset={8}
          classNames={{ content: "rounded-lg bg-theme-primary px-2.5 py-1.5 text-xs text-surface shadow-md" }}
        >
          <span className="ml-auto flex shrink-0 items-center gap-1.5 px-1.5">
            <span className="text-xs text-theme-primary/45">{t("canvas")}</span>
            <Switch
              size="sm"
              isSelected={prefs.canvasMode}
              onValueChange={(v) => patch({ canvasMode: v })}
              aria-label={t("canvas")}
              classNames={{
                wrapper: "group-data-[selected=true]:bg-purple-blue",
              }}
            />
          </span>
        </Tooltip>
      )}

      {pickerOpen && (
        <SelectImageModal
          isOpen={pickerOpen}
          onOpenChange={setPickerOpen}
          max={4}
          onConfirm={(images) => patch({ refs: images.slice(0, 4) })}
        />
      )}
    </div>
  )
}
